import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Cloud, Image as ImageIcon, Trash2, RefreshCw, ChevronDown, ChevronUp,
  ShieldCheck, AlertCircle, CheckCircle2, Camera,
} from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { AdminInput } from "@/admin/components/ui";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const ACCEPT_EXT = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_BYTES = 15 * 1024 * 1024; // server enforces too; FE is best-effort

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Build the absolute URL the <img> tag should fetch.
 * - If value is an absolute http(s) URL → use as-is.
 * - If value is a relative /api/... URL → prefix with backend host.
 * - Otherwise → return empty (caller renders the placeholder).
 */
const absUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/")) return `${API_BASE}${v}`;
  return v;
};

const validateFile = (file) => {
  const type = (file.type || "").toLowerCase();
  const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
  if (!ACCEPT.split(",").includes(type) && !ACCEPT_EXT.includes(ext)) {
    return "Unsupported file type. Please upload JPG, JPEG, PNG or WebP.";
  }
  if (file.size > MAX_BYTES) {
    return `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 15 MB.`;
  }
  if (!file.size) {
    return "The selected file appears to be empty.";
  }
  return null;
};

/**
 * Premium cover-image upload card for the Admin Event Form.
 *
 * Props:
 *  - eventId: string | null   (null = event not saved yet → defer upload)
 *  - value: string            current cover_image URL (proxy or manual)
 *  - manualValue: string      current manual URL value
 *  - onChange({ cover_image, cover_image_manual }) — pushes both fields back into the parent form
 *  - disabled: boolean
 */
export default function CoverImageUploader({
  eventId,
  value,
  manualValue,
  onChange,
  disabled = false,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [removing, setRemoving] = useState(false);

  const previewSrc = absUrl(value);

  const handleFiles = async (files) => {
    const file = (files || [])[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }

    if (!eventId) {
      // Event hasn't been saved yet → store nothing; the parent form will tell the
      // admin to save the draft first. We pre-validate the file silently above so
      // the admin gets a friendly hint instead of a silent no-op.
      toast.message("Save the event as a draft first, then upload a cover image.");
      return;
    }

    setUploading(true);
    setProgress(1);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await adminApi.post(`/admin/events/${eventId}/cover`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const url = r.data?.cover_image || r.data?.cover_image_url || "";
      onChange?.({ cover_image: url, cover_image_manual: "" });
      toast.success("Cover image uploaded.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 413) toast.error("Cover image exceeds 15 MB limit.");
      else if (status === 400) toast.error(formatErr(e) || "We could not process this image.");
      else if (status === 503) toast.error("Cover image storage is not configured yet.");
      else toast.error(formatErr(e) || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    if (disabled || uploading) return;
    if (e.dataTransfer?.files) handleFiles(e.dataTransfer.files);
  };

  const remove = async () => {
    if (!eventId) {
      // For unsaved events: just clear the local field
      onChange?.({ cover_image: "", cover_image_manual: "" });
      return;
    }
    if (!window.confirm("Remove this cover image? The event will fall back to the premium placeholder.")) return;
    setRemoving(true);
    try {
      await adminApi.delete(`/admin/events/${eventId}/cover`);
      onChange?.({ cover_image: "", cover_image_manual: "" });
      toast.success("Cover image removed.");
    } catch (e) {
      toast.error(formatErr(e) || "We could not remove the cover image.");
    } finally {
      setRemoving(false);
    }
  };

  const hasImage = !!previewSrc;

  return (
    <div data-testid="event-cover-uploader" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] tracking-luxe uppercase text-white/45">— Event Cover Image</p>
          <p className="text-sm text-white/65 mt-1 font-body">
            This image appears on the public Events grid and at the top of the Event Detail page.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/45">
          <ShieldCheck className="w-3 h-3 text-[#ff2e93]" /> Admin-only · stored privately
        </div>
      </div>

      {/* Preview + dropzone */}
      <div
        data-testid="cover-dropzone"
        onDragOver={(e) => { e.preventDefault(); if (!disabled && !uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => { if (!disabled && !uploading) fileRef.current?.click(); }}
        className={`relative rounded-md overflow-hidden chrome-border transition-all cursor-pointer ${
          dragOver ? "bg-[#18d3ff]/5" : "glass"
        } ${disabled || uploading ? "opacity-60 cursor-progress" : ""}`}
        style={{ aspectRatio: "16 / 9" }}
      >
        {hasImage ? (
          <img
            data-testid="cover-preview-img"
            src={previewSrc}
            alt="Event cover preview"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div data-testid="cover-placeholder" className="absolute inset-0 flex flex-col items-center justify-center text-white/40 bg-gradient-to-br from-black/50 to-[#0a0a12]">
            <div className="w-14 h-14 rounded-full chrome-border bg-black/40 flex items-center justify-center mb-3">
              <Camera className="w-5 h-5 text-[#18d3ff]" />
            </div>
            <p className="font-display text-xl text-chrome">Premium cover placeholder</p>
            <p className="text-xs text-white/55 mt-1 font-body">Upload a 16:9 cover image to brand this event.</p>
          </div>
        )}

        {/* Hover overlay with primary CTA */}
        {!uploading && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity ${
            hasImage ? "opacity-0 hover:opacity-100" : "opacity-0"
          }`} style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.75))" }}>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white">
              <RefreshCw className="w-3 h-3" /> Replace cover image
            </div>
            <p className="text-[10px] tracking-luxe uppercase text-white/70">Drag & drop or click</p>
          </div>
        )}

        {/* Upload progress bar */}
        {uploading && (
          <div data-testid="cover-uploading" className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm gap-3">
            <Cloud className="w-6 h-6 text-[#18d3ff] animate-pulse" />
            <p className="text-[10px] tracking-luxe uppercase text-white/85">Uploading… {progress}%</p>
            <div className="w-2/3 h-1 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #ff2e93, #18d3ff)",
              }} />
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid="cover-file-input"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="cover-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || uploading}
          className="btn-primary !py-2 !px-4 !text-[10px] disabled:opacity-60"
        >
          <Upload className="w-3.5 h-3.5" />
          {hasImage ? "Replace Cover Image" : "Upload Cover Image"}
        </button>
        {hasImage && (
          <button
            type="button"
            data-testid="cover-remove-btn"
            onClick={remove}
            disabled={disabled || uploading || removing}
            className="btn-ghost !py-2 !px-4 !text-[10px] disabled:opacity-60"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {removing ? "Removing…" : "Remove"}
          </button>
        )}
        <div className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/45">
          <ImageIcon className="w-3 h-3" /> JPG · JPEG · PNG · WebP · up to 15 MB
        </div>
      </div>

      {/* Hint card */}
      <div className="glass chrome-border rounded-md p-3 flex items-start gap-3 text-[11px] text-white/60 font-body">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#7fe6ff] mt-0.5 shrink-0" />
        <p>
          Recommended ratio <strong>16:9</strong> (e.g. 1920×1080). The image is automatically
          optimised for the web and cropped responsively on the Events grid and Event Detail page.
        </p>
      </div>

      {/* Advanced fallback — manual URL */}
      <div className="rounded-md border border-white/8 overflow-hidden">
        <button
          type="button"
          data-testid="cover-advanced-toggle"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white"
        >
          <span className="inline-flex items-center gap-2">
            <AlertCircle className="w-3 h-3" /> Advanced · Use a manual cover image URL
          </span>
          {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showAdvanced && (
          <div className="px-4 pb-4 pt-1 space-y-3" data-testid="cover-advanced-panel">
            <AdminInput
              data-testid="cover-manual-url"
              label="Cover Image URL (advanced fallback)"
              value={manualValue || ""}
              onChange={(e) => onChange?.({ cover_image_manual: e.target.value })}
              placeholder="https://…"
            />
            <p className="text-[10px] tracking-luxe uppercase text-white/40">
              Optional. Use this only if you want to point to an externally hosted image
              instead of an uploaded one. Saving the event will use this URL when no upload
              has been made.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
